import PropTypes from "prop-types";
import React, { useEffect, useState, useRef } from "react";
import fetch from "lib/fetch";
import { stringify } from "querystring";
import { AgGridReact } from "ag-grid-react";
import { GridApi, ColumnApi } from "ag-grid-community";
import { connect } from "react-redux";
import siteConfig from "site-config";
import { format } from "lib/globalize-util";
import moment from "moment";
import _ from "lodash";
// eslint-disable-next-line import-x/no-duplicates
import { LicenseManager } from "ag-grid-enterprise";
// eslint-disable-next-line import-x/no-duplicates
import "ag-grid-enterprise";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import "styles/ag-grid/permissionsTable.styl";
import theme from "styles/theme";

LicenseManager.setLicenseKey(siteConfig.AG_GRID_LICENSE_KEY);

const MAX_SELECTED_ROWS = 20;
const PAGE_SIZE = 50;

const styles = {
    base: {
        padding: "0 8px",
        position: "relative" as "relative",
        overflowY: "auto" as "auto",
    },
    loadingContainer: {
        padding: "20px 0",
        textAlign: "center" as "center",
        width: "100%",
        float: "none" as "none",
    },
    noResults: {
        textAlign: "center",
        padding: "20px 10px",
        fontSize: "20px",
    },
    checkboxIcon: { height: "20px", width: "20px" },
};

const BulkCatalogs = (props) => {
    const {
        catalogType,
        sortBy,
        sortOrder,
        active,
        listSearch,
        onSelectionChange,
        createdCatalogs,
        isFetchingCatalogs,
        selectedCatalogs,
        scope,
        viewportHeight,
    } = props;
    const [sort, setSort] = useState("none");
    const [rowData, setRowData] = useState([]);
    const [initialRowData, setInitialRowData] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [checked, setChecked] = useState(false);

    const agGridApiRef = useRef<GridApi>(null);
    const columnApiRef = useRef<ColumnApi>(null);
    const checkboxRef = useRef(null);

    const term = React.useMemo(
        () =>
            catalogType === "custom-list"
                ? format("list")
                : scope.brand.hasPreference("linesheets_are_catalogs")
                  ? format("catalog")
                  : format("linesheet"),
        [scope, catalogType],
    );
    const pluralTerm = React.useMemo(
        () =>
            catalogType === "custom-list"
                ? format("lists")
                : scope.brand.hasPreference("linesheets_are_catalogs")
                  ? format("catalogs")
                  : format("linesheets"),
        [scope, catalogType],
    );
    const checkboxTooltipText = {
        none: format("No sort (default)"),
        desc: format("Show all selected at the top"),
        asc: format("Show all selected at the bottom"),
    };

    const CheckboxHeader = ({ api, column }) => {
        const updateCheckboxState = () => {
            const selectedNodes = api.getSelectedNodes();

            let newCheckedState;
            if (selectedNodes.length === 0) {
                newCheckedState = false;
            } else {
                newCheckedState = null;
            }

            if (newCheckedState !== checked) {
                setChecked(newCheckedState);
            }
        };

        const onCheckboxChange = () => {
            api.deselectAll();
        };

        useEffect(() => {
            api.addEventListener("selectionChanged", updateCheckboxState);
            return () => {
                api.removeEventListener("selectionChanged", updateCheckboxState);
            };
        }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

        useEffect(() => {
            if (checkboxRef.current) {
                checkboxRef.current.indeterminate = checked === null;
            }
        }, [checked]); // eslint-disable-line react-hooks/exhaustive-deps

        const onHeaderClick = (event) => {
            if (event.target.tagName === "INPUT") {
                return;
            }

            const currentSort = column.getSort();
            let nextSort = "none";

            switch (currentSort) {
                case "desc":
                    nextSort = "asc";
                    break;
                case "asc":
                    nextSort = "none";
                    break;
                default:
                    nextSort = "desc";
                    break;
            }
            setSort(nextSort);

            column.columnApi.applyColumnState({
                state: [{ colId: "checkbox", sort: nextSort }],
                defaultState: { sort: null },
            });
        };

        return (
            <div
                style={{ display: "flex", alignItems: "center", width: "100%", height: "100%" }}
                onClick={onHeaderClick}
            >
                <input
                    ref={checkboxRef}
                    type="checkbox"
                    checked={checked || false}
                    onChange={onCheckboxChange}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "2px",
                        position: "relative",
                        marginRight: "10px",
                    }}
                />
                <span className={`ag-header-icon ag-header-label-icon ag-sort-${sort}-icon`} aria-hidden="true">
                    <span className={`ag-icon ag-icon-${sort}`} unselectable="on" role="presentation"></span>
                </span>
            </div>
        );
    };

    const dateRenderer = (catalog) => {
        if (_.isEmpty(catalog.data)) {
            return null;
        }
        return (
            <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "12px" }}>{catalog.data.modifiedOn}</span>
                <span style={{ fontSize: "10px", color: theme.palette.darkGrey }}>
                    {format(`Created ${catalog.data.createdOn}`)}
                </span>
            </div>
        );
    };

    const colDefs = [
        {
            headerName: "",
            field: "checkbox",
            checkboxSelection: true,
            headerComponent: CheckboxHeader,
            valueGetter: (catalog) => selectedCatalogs.includes(catalog?.data?.id) || false,
            sortable: true,
            comparator: (valueA, valueB, nodeA, nodeB) => {
                const selectedA = nodeA.isSelected() ? 1 : 0;
                const selectedB = nodeB.isSelected() ? 1 : 0;
                return selectedA - selectedB;
            },
            width: 100,
            headerTooltip: checkboxTooltipText[sort],
        },
        {
            headerName: _.capitalize(term),
            valueGetter: (catalog) => catalog?.data?.title,
            field: "title",
            sortable: true,
        },
        {
            headerName: format("Last modified"),
            field: "modified_on",
            sortable: true,
            valueGetter: (catalog) => catalog?.data,
            comparator: (valueA, valueB, nodeA, nodeB) => {
                if (nodeA.data.modified_on == nodeB.data.modified_on) {
                    return 0;
                }

                return nodeA.data.modified_on > nodeB.data.modified_on ? 1 : -1;
            },
            cellRenderer: dateRenderer,
        },
    ];

    const defaultColDef = {
        resizable: true,
        sortable: true,
        filter: true,
        suppressMenu: true,
        suppressMovable: true,
        suppressMultiSort: true,
        alwaysMultiSort: false,
    };

    const fetchCatalogsData = (reset = false) => {
        const query = {
            __limit: PAGE_SIZE,
            __company: scope?.viewAs?._id,
            __sort: "modified_on",
            __sort_order: "DESC",
            __meta: true,
            type: catalogType,
            ...(listSearch && { __search: listSearch }),
            ...(active !== undefined && { active }),
            ...(catalogType === "custom-list" && { shared: false }),
        };
        const endpoint = !reset && nextPage ? nextPage : `/api/v3.1/catalogs?${stringify(query)}`;

        fetch(`${siteConfig.api}${endpoint}`, {
            method: "GET",
            noDomain: true,
            headers: {
                "Active-Brand": scope?.brand?._id,
            },
        })
            .then((response) => {
                return response.json().then((data) => {
                    const nextPage = response.headers.get("Next");
                    setNextPage(nextPage);
                    return data;
                });
            })
            .then((data) => {
                const catalogs = data.map((catalog) => ({
                    ...catalog,
                    modifiedOn: moment(catalog.modified_on).format("MMM DD, YYYY"),
                    createdOn: moment(catalog.created_on).format("MMM DD, YYYY"),
                }));
                if (reset) {
                    setRowData(catalogs);
                    setInitialRowData(catalogs);
                }
                if (agGridApiRef.current && data.length > 0) {
                    agGridApiRef.current.resetRowHeights();
                    setRowData((prevData) => [...prevData, ...catalogs]);
                    if (!reset) {
                        agGridApiRef.current.applyTransaction({ add: catalogs });
                    }
                    const selectedNodes = agGridApiRef.current.getSelectedNodes();
                    const selectedNodesIds = selectedNodes.map((node) => node.data.id);
                    const selectedCatalogIds = selectedCatalogs.map((selectedCatalog) => selectedCatalog.id);
                    let selectedCount = selectedNodes.length;
                    if (!_.isEqual(selectedCatalogIds, selectedNodesIds) && selectedCount < MAX_SELECTED_ROWS) {
                        agGridApiRef.current.forEachNode((node) => {
                            if (selectedCount === MAX_SELECTED_ROWS) {
                                return;
                            }
                            if (!node.isSelected() && selectedCatalogIds.includes(node.data.id)) {
                                node.setSelected(true);
                                selectedCount++;
                            }
                        });
                    }
                }
            });
    };

    useEffect(() => {
        fetchCatalogsData(true);
        if (columnApiRef.current) {
            columnApiRef.current.applyColumnState({
                state: [{ colId: "modified_on", sort: "desc" as "desc" }],
                defaultState: { sort: null },
                applyOrder: false,
            });
        }
    }, [listSearch, sortBy, sortOrder, createdCatalogs]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleResize = () => {
            if (agGridApiRef.current) {
                agGridApiRef.current.sizeColumnsToFit();
            }
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            agGridApiRef.current = null;
        };
    }, []);

    const onGridReady = (params) => {
        agGridApiRef.current = params.api;
        columnApiRef.current = params.columnApi;
        params.api.sizeColumnsToFit();
    };

    const onSelectionChanged = () => {
        const selectedNodes = agGridApiRef.current.getSelectedNodes();
        const latestSelectedRows = selectedNodes.map((node) => node.data);
        const added = latestSelectedRows.filter((row) => !selectedCatalogs.some((catalog) => catalog.id === row.id));
        const removed = selectedCatalogs.filter(
            (catalog) =>
                rowData.some((row) => row.id === catalog.id) &&
                !latestSelectedRows.some((row) => row.id === catalog.id),
        );

        agGridApiRef.current.forEachNode((node) => {
            if (!node.isSelected() && latestSelectedRows.length === MAX_SELECTED_ROWS) {
                node.selectable = false;
            } else {
                node.selectable = true;
            }
        });

        onSelectionChange({ added, removed });
    };

    const onBodyScrollEnd = () => {
        const gridBody = agGridApiRef.current.getVerticalPixelRange();
        const scrollHeight = agGridApiRef.current.getDisplayedRowCount() * 60;
        if (nextPage && !isFetchingCatalogs && gridBody.bottom >= scrollHeight - 60) {
            fetchCatalogsData();
        }
    };

    const onSortChanged = () => {
        if (columnApiRef.current) {
            const checkboxSortState = columnApiRef.current.getColumnState().find((col) => col.colId === "checkbox");
            const activeSortState = columnApiRef.current
                .getColumnState()
                .find((col) => col.colId !== "checkbox" && ["asc", "desc"].includes(col.sort));
            if (
                checkboxSortState &&
                checkboxSortState.sort !== undefined &&
                activeSortState &&
                activeSortState.sort !== undefined
            ) {
                columnApiRef.current.applyColumnState({
                    state: [{ colId: "checkbox", sort: undefined }],
                    applyOrder: false,
                });
                setSort("none");
            }
        }
    };

    return (
        <div
            style={{ ...styles.base, height: viewportHeight > 900 ? "49vh" : "35vh" }}
            className="ag-theme-balham permissionsGrid"
        >
            <AgGridReact
                overlayNoRowsTemplate={format(`No available ${pluralTerm}. Select + New ${term} to create one.`)}
                rowData={initialRowData}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                rowSelection="multiple"
                onGridReady={onGridReady}
                onBodyScrollEnd={onBodyScrollEnd}
                onSelectionChanged={onSelectionChanged}
                onSortChanged={onSortChanged}
                suppressRowClickSelection={true}
                headerHeight={44}
                rowHeight={60}
                rowBuffer={PAGE_SIZE}
                cacheBlockSize={PAGE_SIZE}
                paginationPageSize={PAGE_SIZE}
                pagination={false}
                animateRows={true}
                suppressContextMenu={true}
            />
        </div>
    );
};

BulkCatalogs.propTypes = {
    filters: PropTypes.object,
    searchTerm: PropTypes.string,
    listSearch: PropTypes.string,
    catalogType: PropTypes.string.isRequired,
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string,
    active: PropTypes.bool,
    onGridReady: PropTypes.func,
    selectedCatalogs: PropTypes.array,
    onSelectionChange: PropTypes.func,
    createdCatalogs: PropTypes.array,
    scope: PropTypes.any,
    viewportHeight: PropTypes.number,
};

const mapStateToProps = (state) => ({
    scope: state.scope,
    viewportHeight: state.app.height,
});

export default connect(mapStateToProps, null)(BulkCatalogs);
